import { useState, useEffect } from 'react';
import { useAuth } from '../AuthContext';
import { useNavigate } from 'react-router-dom';
import {
  Users as UsersIcon, Edit, Plus, RefreshCw, Shield, UserCheck, UserX,
  Calendar, X, Check, AlertTriangle, Search, Filter, Download, MoreVertical,
  ChevronUp, ChevronDown, ChevronLeft, ChevronRight, Eye, EyeOff, Key, FileText, History, ArrowLeft,
  Sparkles
} from 'lucide-react';
import toast, { Toaster } from 'react-hot-toast';
import API_URL from '../config';

const API_BASE = API_URL;

const WEEK_DAYS = [
  { value: 0, label: 'Lun' },
  { value: 1, label: 'Mar' },
  { value: 2, label: 'Mer' },
  { value: 3, label: 'Jeu' },
  { value: 4, label: 'Ven' },
  { value: 5, label: 'Sam' },
  { value: 6, label: 'Dim' },
];

function WorkDaysCheckboxes({ value, onChange }) {
  const selected = Array.isArray(value) ? value : [];
  const toggle = (day) => {
    const next = selected.includes(day)
      ? selected.filter(d => d !== day)
      : [...selected, day].sort((a, b) => a - b);
    onChange(next);
  };
  return (
    <div className="flex flex-wrap gap-2">
      {WEEK_DAYS.map(({ value: day, label }) => (
        <button
          key={day}
          type="button"
          onClick={() => toggle(day)}
          className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
            selected.includes(day)
              ? 'bg-indigo-600 text-white border-indigo-600'
              : 'bg-gray-50 text-gray-600 border-gray-200 hover:border-indigo-300'
          }`}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

export default function GestionUtilisateurs() {
  const { isManager } = useAuth();
  const navigate = useNavigate();

  const [users, setUsers] = useState([]);
  const [balances, setBalances] = useState({});
  const [loading, setLoading] = useState(true);
  const [warningDays, setWarningDays] = useState(14);
  const [showModal, setShowModal] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [smicBase, setSmicBase] = useState(1823.03);

  useEffect(() => {
    const fetchSmic = async () => {
      try {
        const response = await fetch('https://mon-entreprise.urssaf.fr/api/v1/evaluate', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
          },
          body: JSON.stringify({
            expressions: ['SMIC'],
            situation: {},
          }),
        });
        if (response.ok) {
          const data = await response.json();
          const smicVal = data.evaluate?.[0]?.nodeValue;
          if (smicVal && typeof smicVal === 'number' && smicVal > 0) {
            setSmicBase(smicVal);
          }
        }
      } catch (err) {
        console.warn("Impossible de récupérer le SMIC en direct, utilisation de la valeur par défaut :", err);
      }
    };
    fetchSmic();
  }, []);

  // New States for UX/UI improvements
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('ALL');
  const [contractFilter, setContractFilter] = useState('ALL');
  const [statusTab, setStatusTab] = useState('ALL'); // 'ALL' | 'ACTIVE' | 'INACTIVE'
  const [sortConfig, setSortConfig] = useState({ key: 'full_name', direction: 'asc' });
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  
  const [showPassword, setShowPassword] = useState(false);
  const [actionMenuOpenId, setActionMenuOpenId] = useState(null);

  const [modalTab, setModalTab] = useState('form'); // 'form' | 'history'
  const [contractVersions, setContractVersions] = useState([]);

  const [showAvenantModal, setShowAvenantModal] = useState(false);
  const [avenantUser, setAvenantUser] = useState(null);
  const [avenantForm, setAvenantForm] = useState({
    effective_from: '',
    note: '',
    contract_type: '',
    monthly_gross_salary: '',
    employer_charges: '',
    weekly_hours: '',
    work_days: [],
    contract_start_date: '',
    contract_end_date: '',
    trial_period_end_date: '',
  });
  const [submittingAvenant, setSubmittingAvenant] = useState(false);

  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    password: '',
    role: 'EMPLOYEE',
    is_active: true,
    leave_balance: 0,
    contract_type: '',
    contract_start_date: '',
    contract_end_date: '',
    trial_period_end_date: '',
    weekly_hours: '',
    work_days: [],
    monthly_gross_salary: '',
    employer_charges: '',
  });

  // Redirect if not manager
  useEffect(() => {
    if (!isManager) {
      navigate('/gestionShifts');
    }
  }, [isManager, navigate]);

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/users/`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Erreur lors de la récupération des utilisateurs');
      const data = await response.json();
      setUsers(data);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Erreur lors du chargement des utilisateurs');
    }
  };

  const fetchBalances = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/leaves/balances`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Erreur lors de la récupération des soldes');
      const data = await response.json();
      const balanceMap = {};
      data.forEach(b => balanceMap[b.user_id] = b);
      setBalances(balanceMap);
    } catch (error) {
      console.error('Error fetching balances:', error);
    }
  };

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const fetchConfig = async () => {
        try {
          const token = localStorage.getItem('token');
          const res = await fetch(`${API_BASE}/config/public`, {
            headers: { 'Authorization': `Bearer ${token}` },
          });
          if (res.ok) {
            const data = await res.json();
            setWarningDays(data.trial_period_warning_days ?? 14);
          }
        } catch {
          // silently fall back to default 14
        }
      };
      await Promise.all([fetchUsers(), fetchBalances(), fetchConfig()]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isManager) {
      fetchAllData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isManager]);

  // Click outside to close action menu
  useEffect(() => {
    const handleClickOutside = () => setActionMenuOpenId(null);
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const fetchContractVersions = async (userId) => {
    if (!userId) return;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/users/${userId}/contract-versions`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setContractVersions(data);
      }
    } catch {
      // silently fail
    }
  };

  const handleCreate = () => {
    setIsCreating(true);
    setEditingUser(null);
    setShowPassword(false);
    setModalTab('form');
    setContractVersions([]);
    setFormData({
      full_name: '',
      email: '',
      password: '',
      role: 'EMPLOYEE',
      is_active: true,
      leave_balance: 0,
      contract_type: '',
      contract_start_date: '',
      contract_end_date: '',
      trial_period_end_date: '',
      weekly_hours: '',
      work_days: [],
      monthly_gross_salary: '',
      employer_charges: '',
    });
    setShowModal(true);
  };

  const handleEdit = (userToEdit) => {
    setIsCreating(false);
    setEditingUser(userToEdit);
    setShowPassword(false);
    setModalTab('form');
    setContractVersions([]);
    setFormData({
      full_name: userToEdit.full_name || '',
      email: userToEdit.email,
      password: '',
      role: userToEdit.role,
      is_active: userToEdit.is_active,
      leave_balance: balances[userToEdit.id]?.available_days || 0,
      contract_type: userToEdit.contract_type || '',
      contract_start_date: userToEdit.contract_start_date || '',
      contract_end_date: userToEdit.contract_end_date || '',
      trial_period_end_date: userToEdit.trial_period_end_date || '',
      weekly_hours: userToEdit.weekly_hours || '',
      work_days: userToEdit.work_days || [],
      monthly_gross_salary: userToEdit.monthly_gross_salary || '',
      employer_charges: userToEdit.employer_charges || '',
    });
    setShowModal(true);
  };

  const handleOpenAvenant = (user) => {
    setAvenantUser(user);
    setAvenantForm({
      effective_from: new Date().toISOString().split('T')[0],
      note: '',
      contract_type: user.contract_type || '',
      monthly_gross_salary: user.monthly_gross_salary || '',
      employer_charges: user.employer_charges || '',
      weekly_hours: user.weekly_hours || '',
      work_days: user.work_days || [],
      contract_start_date: user.contract_start_date || '',
      contract_end_date: user.contract_end_date || '',
      trial_period_end_date: user.trial_period_end_date || '',
    });
    setShowAvenantModal(true);
  };

  const handleSubmitAvenant = async (e) => {
    e.preventDefault();
    if (!avenantForm.effective_from) {
      toast.error("La date d'effet est obligatoire.");
      return;
    }
    setSubmittingAvenant(true);
    try {
      const token = localStorage.getItem('token');
      const payload = {
        effective_from: avenantForm.effective_from,
        note: avenantForm.note || null,
        contract_type: avenantForm.contract_type || null,
        monthly_gross_salary: avenantForm.monthly_gross_salary ? parseFloat(avenantForm.monthly_gross_salary) : null,
        employer_charges: avenantForm.employer_charges ? parseFloat(avenantForm.employer_charges) : null,
        weekly_hours: avenantForm.weekly_hours ? parseFloat(avenantForm.weekly_hours) : null,
        work_days: avenantForm.work_days && avenantForm.work_days.length > 0 ? avenantForm.work_days : null,
        contract_start_date: avenantForm.contract_start_date || null,
        contract_end_date: avenantForm.contract_end_date || null,
        trial_period_end_date: avenantForm.trial_period_end_date || null,
      };
      const res = await fetch(`${API_BASE}/users/${avenantUser.id}/contract-versions`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || "Erreur lors de la création de l'avenant");
      }
      toast.success('Avenant créé avec succès !');
      setShowAvenantModal(false);
      await fetchAllData();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSubmittingAvenant(false);
    }
  };

  const handleDeactivate = async (userToDeactivate) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir ${userToDeactivate.is_active ? 'désactiver' : 'réactiver'} le compte de ${userToDeactivate.full_name || userToDeactivate.email} ?`)) {
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/users/${userToDeactivate.id}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ is_active: !userToDeactivate.is_active }),
      });
      
      if (!response.ok) throw new Error('Erreur lors du changement de statut');
      toast.success(`Utilisateur ${userToDeactivate.is_active ? 'désactivé' : 'réactivé'} avec succès`);
      fetchAllData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const token = localStorage.getItem('token');

      if (isCreating) {
        const response = await fetch(`${API_BASE}/auth/register`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({
            email: formData.email,
            password: formData.password,
            full_name: formData.full_name,
            role: formData.role,
            contract_type: formData.contract_type || null,
            contract_start_date: formData.contract_start_date || null,
            contract_end_date: formData.contract_end_date || null,
            trial_period_end_date: formData.trial_period_end_date || null,
            weekly_hours: formData.weekly_hours ? parseFloat(formData.weekly_hours) : null,
            work_days: formData.work_days && formData.work_days.length > 0 ? formData.work_days : null,
            monthly_gross_salary: formData.monthly_gross_salary ? parseFloat(formData.monthly_gross_salary) : null,
            employer_charges: formData.employer_charges ? parseFloat(formData.employer_charges) : null,
          }),
        });

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.detail || 'Erreur lors de la création');
        }

        const newUser = await response.json();

        if (formData.leave_balance !== 0) {
          await fetch(`${API_BASE}/api/leaves/balance/${newUser.id}`, {
            method: 'PUT',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ available_days: formData.leave_balance }),
          });
        }
        toast.success('Utilisateur créé avec succès !');
      } else {
        const updatePayload = {
          full_name: formData.full_name,
          email: formData.email,
          role: formData.role,
          is_active: formData.is_active,
          contract_type: formData.contract_type || null,
          contract_start_date: formData.contract_start_date || null,
          contract_end_date: formData.contract_end_date || null,
          trial_period_end_date: formData.trial_period_end_date || null,
          weekly_hours: formData.weekly_hours ? parseFloat(formData.weekly_hours) : null,
          work_days: formData.work_days && formData.work_days.length > 0 ? formData.work_days : null,
          monthly_gross_salary: formData.monthly_gross_salary ? parseFloat(formData.monthly_gross_salary) : null,
          employer_charges: formData.employer_charges ? parseFloat(formData.employer_charges) : null,
        };

        if (formData.password.trim()) {
          updatePayload.password = formData.password;
        }

        const response = await fetch(`${API_BASE}/users/${editingUser.id}`, {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(updatePayload),
        });

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.detail || 'Erreur lors de la mise à jour');
        }

        const currentBalance = balances[editingUser.id]?.available_days || 0;
        if (parseFloat(formData.leave_balance) !== parseFloat(currentBalance)) {
          await fetch(`${API_BASE}/api/leaves/balance/${editingUser.id}`, {
            method: 'PUT',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ available_days: formData.leave_balance }),
          });
        }
        toast.success('Utilisateur mis à jour avec succès !');
      }

      await fetchAllData();
      setShowModal(false);
    } catch (error) {
      console.error('Error saving user:', error);
      toast.error(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleExportCSV = () => {
    const headers = ['Nom', 'Email', 'Role', 'Contrat', 'Taux Horaire', 'Conges', 'Statut'];
    const rows = filteredUsers.map(u => [
      `"${u.full_name || ''}"`,
      `"${u.email}"`,
      u.role,
      u.contract_type || '',
      u.monthly_gross_salary || '',
      balances[u.id]?.available_days || 0,
      u.is_active ? 'Actif' : 'Inactif'
    ]);
    const csvContent = "data:text/csv;charset=utf-8,\uFEFF" 
      + [headers.join(','), ...rows.map(e => e.join(','))].join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "utilisateurs.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('Export réussi !');
  };

  const generatePassword = () => {
    const chars = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*";
    let pass = "";
    for (let i = 0; i < 10; i++) {
      pass += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setFormData({ ...formData, password: pass });
    setShowPassword(true);
  };

  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getInitials = (name, email) => {
    if (name) {
      const parts = name.trim().split(' ');
      if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
      return name.substring(0, 2).toUpperCase();
    }
    return email.substring(0, 2).toUpperCase();
  };

  if (!isManager) {
    return null;
  }

  // Derived computations
  const stats = {
    total: users.length,
    managers: users.filter(u => u.role === 'MANAGER').length,
    active: users.filter(u => u.is_active).length,
  };

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const in14 = new Date(today);
  in14.setDate(in14.getDate() + 14);
  const expiringUsers = users.filter(u => {
    if (!u.contract_end_date) return false;
    const end = new Date(u.contract_end_date);
    end.setHours(0, 0, 0, 0);
    return end >= today && end <= in14;
  }).sort((a, b) => new Date(a.contract_end_date) - new Date(b.contract_end_date));

  const inN = new Date(today);
  inN.setDate(inN.getDate() + warningDays);
  const trialEndingSoon = users.filter(u => {
    if (!u.trial_period_end_date) return false;
    const end = new Date(u.trial_period_end_date);
    end.setHours(0, 0, 0, 0);
    return end >= today && end <= inN;
  }).sort((a, b) => new Date(a.trial_period_end_date) - new Date(b.trial_period_end_date));

  // Filtering
  let filteredUsers = users.filter(u => {
    if (statusTab === 'ACTIVE' && !u.is_active) return false;
    if (statusTab === 'INACTIVE' && u.is_active) return false;
    if (roleFilter !== 'ALL' && u.role !== roleFilter) return false;
    if (contractFilter !== 'ALL' && u.contract_type !== contractFilter) return false;
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      const matchName = u.full_name?.toLowerCase().includes(term);
      const matchEmail = u.email?.toLowerCase().includes(term);
      if (!matchName && !matchEmail) return false;
    }
    return true;
  });

  // Sorting
  if (sortConfig.key) {
    filteredUsers.sort((a, b) => {
      let aVal = a[sortConfig.key];
      let bVal = b[sortConfig.key];
      
      if (sortConfig.key === 'leave_balance') {
        aVal = balances[a.id]?.available_days ?? 0;
        bVal = balances[b.id]?.available_days ?? 0;
      }
      
      if (typeof aVal === 'string') aVal = aVal.toLowerCase();
      if (typeof bVal === 'string') bVal = bVal.toLowerCase();
      
      if (aVal === null || aVal === undefined) aVal = '';
      if (bVal === null || bVal === undefined) bVal = '';

      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }

  // Pagination
  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage) || 1;
  const paginatedUsers = filteredUsers.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const SortIcon = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) return <div className="w-4 h-4 text-transparent group-hover:text-gray-300"><ChevronUp className="w-4 h-4" /></div>;
    return sortConfig.direction === 'asc' ? <ChevronUp className="w-4 h-4 text-indigo-500" /> : <ChevronDown className="w-4 h-4 text-indigo-500" />;
  };

  const SkeletonRow = () => (
    <tr className="animate-pulse">
      <td className="px-6 py-4"><div className="flex items-center gap-3"><div className="w-10 h-10 bg-gray-200 rounded-full"></div><div><div className="h-4 bg-gray-200 rounded w-24 mb-2"></div><div className="h-3 bg-gray-200 rounded w-32"></div></div></div></td>
      <td className="px-6 py-4"><div className="h-6 bg-gray-200 rounded-full w-20"></div></td>
      <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-16"></div></td>
      <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-12"></div></td>
      <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-16"></div></td>
      <td className="px-6 py-4"><div className="h-6 bg-gray-200 rounded-full w-16"></div></td>
      <td className="px-6 py-4"><div className="h-8 bg-gray-200 rounded w-8 ml-auto"></div></td>
    </tr>
  );

  return (
    <div className="min-h-screen bg-gray-50 pb-12">
      <Toaster position="top-right" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <button onClick={() => navigate(-1)} className="p-2 hover:bg-white hover:shadow-sm rounded-xl transition-all text-gray-500 flex-shrink-0">
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="p-2 bg-indigo-100 rounded-xl">
                <UsersIcon className="w-6 h-6 text-indigo-600" />
              </div>
              <h1 className="text-2xl font-extrabold text-[#1A1B4B]">Gestion des Utilisateurs</h1>
            </div>
            <p className="text-gray-500 text-sm ml-1">Gérez les comptes et permissions de l'équipe</p>
          </div>
          <div className="flex flex-wrap gap-2 sm:gap-3">
            <button
              onClick={handleExportCSV}
              className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50 shadow-sm transition-colors"
            >
              <Download className="w-4 h-4" />
              <span className="hidden sm:inline">Exporter</span>
            </button>
            <button
              onClick={fetchAllData}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50 shadow-sm transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              <span className="hidden sm:inline">Actualiser</span>
            </button>
            <button
              onClick={handleCreate}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-xl text-sm font-medium hover:bg-indigo-700 transition-colors shadow-sm"
            >
              <Plus className="w-4 h-4" />
              Nouvel Utilisateur
            </button>
          </div>
        </div>

        {/* Statistics */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 flex items-center gap-3">
            <div className="w-10 h-10 bg-indigo-50 rounded-xl flex items-center justify-center flex-shrink-0">
              <UsersIcon className="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <div className="text-xs text-gray-500 font-medium">Utilisateurs Total</div>
              <div className="text-2xl font-extrabold text-[#1A1B4B]">{stats.total}</div>
            </div>
          </div>
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-50 rounded-xl flex items-center justify-center flex-shrink-0">
              <Shield className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <div className="text-xs text-gray-500 font-medium">Managers</div>
              <div className="text-2xl font-extrabold text-[#1A1B4B]">{stats.managers}</div>
            </div>
          </div>
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 flex items-center gap-3">
            <div className="w-10 h-10 bg-emerald-50 rounded-xl flex items-center justify-center flex-shrink-0">
              <UserCheck className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <div className="text-xs text-gray-500 font-medium">Comptes Actifs</div>
              <div className="text-2xl font-extrabold text-[#1A1B4B]">{stats.active}</div>
            </div>
          </div>
        </div>

        {/* Alerts */}
        <div className="space-y-4 mb-6">
          {!loading && expiringUsers.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 shadow-sm">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-amber-800 mb-3">
                    {expiringUsers.length === 1
                      ? '1 contrat expire dans moins de 2 semaines'
                      : `${expiringUsers.length} contrats expirent dans moins de 2 semaines`}
                  </p>
                  <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                    {expiringUsers.map(u => {
                      const end = new Date(u.contract_end_date);
                      end.setHours(0, 0, 0, 0);
                      const daysLeft = Math.round((end - today) / (1000 * 60 * 60 * 24));
                      const isUrgent = daysLeft <= 2;
                      return (
                        <div key={u.id} className={`flex items-center justify-between gap-3 p-3 rounded-lg border cursor-pointer transition-all hover:shadow-md ${isUrgent ? 'bg-white border-red-200' : 'bg-white border-amber-200'}`} onClick={() => handleEdit(u)}>
                          <div className="flex items-center gap-3 min-w-0">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${isUrgent ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>
                              {getInitials(u.full_name, u.email)}
                            </div>
                            <div className="min-w-0">
                              <p className={`text-sm font-medium truncate ${isUrgent ? 'text-red-800' : 'text-amber-800'}`}>{u.full_name || u.email}</p>
                              <p className={`text-xs truncate ${isUrgent ? 'text-red-600' : 'text-amber-600'}`}>CDD jusqu'au {end.toLocaleDateString('fr-FR')}</p>
                            </div>
                          </div>
                          <span className={`text-xs font-bold px-2 py-1 rounded-full flex-shrink-0 ${isUrgent ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>
                            {daysLeft === 0 ? "Aujourd'hui" : daysLeft === 1 ? 'Demain' : `J-${daysLeft}`}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          )}

          {!loading && trialEndingSoon.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-2.5 flex items-center gap-3 flex-wrap">
              <div className="flex items-center gap-2 text-amber-700 flex-shrink-0">
                <AlertTriangle className="w-4 h-4" />
                <span className="text-sm font-medium">
                  {trialEndingSoon.length === 1
                    ? `1 période d'essai se termine dans moins de ${warningDays} jours`
                    : `${trialEndingSoon.length} périodes d'essai se terminent dans moins de ${warningDays} jours`}
                </span>
              </div>
              <div className="flex items-center gap-1.5 flex-wrap">
                {trialEndingSoon.map(u => {
                  const end = new Date(u.trial_period_end_date);
                  end.setHours(0, 0, 0, 0);
                  const daysLeft = Math.round((end - today) / (1000 * 60 * 60 * 24));
                  const isUrgent = daysLeft <= 2;
                  const label = daysLeft === 0 ? "Aujourd'hui" : daysLeft === 1 ? 'Demain' : `${daysLeft}j`;
                  return (
                    <button
                      key={u.id}
                      onClick={() => handleEdit(u)}
                      title={`${u.full_name || u.email} — Fin d'essai le ${end.toLocaleDateString('fr-FR')}`}
                      className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium border transition-all hover:shadow-sm cursor-pointer ${isUrgent ? 'bg-red-100 border-red-200 text-red-700 hover:bg-red-200' : 'bg-white border-amber-200 text-amber-700 hover:bg-amber-100'}`}
                    >
                      <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${isUrgent ? 'bg-red-200 text-red-800' : 'bg-amber-200 text-amber-800'}`}>
                        {getInitials(u.full_name, u.email)}
                      </span>
                      <span className="max-w-[80px] truncate">{u.full_name || u.email}</span>
                      <span className={`font-bold ${isUrgent ? 'text-red-600' : 'text-amber-600'}`}>{label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Toolbar (Tabs, Search, Filters) */}
        <div className="bg-white rounded-2xl shadow-sm mb-6 overflow-hidden border border-gray-100">
          <div className="border-b border-gray-100">
            <nav className="flex flex-wrap -mb-px px-4 sm:px-6 gap-4 sm:gap-6">
              {[
                { id: 'ALL', label: 'Tous les utilisateurs' },
                { id: 'ACTIVE', label: 'Actifs' },
                { id: 'INACTIVE', label: 'Inactifs' }
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => { setStatusTab(tab.id); setCurrentPage(1); }}
                  className={`py-4 px-1 inline-flex items-center gap-2 border-b-2 font-medium text-sm transition-colors ${
                    statusTab === tab.id
                      ? 'border-indigo-500 text-indigo-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {tab.id === 'ACTIVE' && <UserCheck className="w-4 h-4" />}
                  {tab.id === 'INACTIVE' && <UserX className="w-4 h-4" />}
                  {tab.label}
                  <span className={`ml-2 py-0.5 px-2.5 rounded-full text-xs font-medium ${
                    statusTab === tab.id ? 'bg-indigo-100 text-indigo-700' : 'bg-gray-100 text-gray-600'
                  }`}>
                    {tab.id === 'ALL' ? users.length : tab.id === 'ACTIVE' ? stats.active : users.length - stats.active}
                  </span>
                </button>
              ))}
            </nav>
          </div>
          
          <div className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 bg-gray-50/50">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input 
                type="text" 
                placeholder="Rechercher (Nom, Email)..."
                value={searchTerm}
                onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-shadow outline-none text-sm"
              />
            </div>
            
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
              <select 
                value={roleFilter}
                onChange={e => { setRoleFilter(e.target.value); setCurrentPage(1); }}
                className="w-full pl-10 pr-8 py-2.5 bg-white border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent appearance-none text-sm outline-none cursor-pointer"
              >
                <option value="ALL">Tous les rôles</option>
                <option value="MANAGER">Manager</option>
                <option value="TEAM_LEADER">Team Leader</option>
                <option value="EMPLOYEE">Employé</option>
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
            </div>

            <div className="relative">
              <Filter className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
              <select 
                value={contractFilter}
                onChange={e => { setContractFilter(e.target.value); setCurrentPage(1); }}
                className="w-full pl-10 pr-8 py-2.5 bg-white border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent appearance-none text-sm outline-none cursor-pointer"
              >
                <option value="ALL">Tous les contrats</option>
                <option value="CDI">CDI</option>
                <option value="CDD">CDD</option>
                <option value="Freelance">Freelance</option>
                <option value="CDI CADRE HS">CDI CADRE HS</option>
                <option value="CDI CADRE NO HS">CDI CADRE NO HS</option>
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
            </div>
          </div>
        </div>

        {/* Users List (Responsive) */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-visible relative">
          
          {/* Desktop Table View */}
          <div className="hidden md:block overflow-x-auto min-h-[400px]">
            <table className="w-full">
              <thead className="bg-gray-50/80 border-b border-gray-100">
                <tr>
                  {[{ key: 'full_name', label: 'Utilisateur' }, 
                    { key: 'role', label: 'Rôle' }, 
                    { key: 'contract_type', label: 'Contrat' },
                    { key: 'monthly_gross_salary', label: 'Salaire Mensuel' },
                    { key: 'leave_balance', label: 'Congés' },
                    { key: 'is_active', label: 'Statut' }].map(col => (
                    <th 
                      key={col.key} 
                      onClick={() => requestSort(col.key)}
                      className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer group hover:bg-gray-100 transition-colors select-none"
                    >
                      <div className="flex items-center gap-2">
                        {col.label}
                        <SortIcon columnKey={col.key} />
                      </div>
                    </th>
                  ))}
                  <th className="px-6 py-4 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {loading ? (
                  Array(5).fill(0).map((_, i) => <SkeletonRow key={i} />)
                ) : paginatedUsers.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-6 py-16 text-center text-gray-500">
                      <UsersIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                      <p className="text-lg font-medium text-gray-900">Aucun utilisateur trouvé</p>
                      <p className="text-sm mt-1">Essayez de modifier vos filtres de recherche.</p>
                    </td>
                  </tr>
                ) : (
                  paginatedUsers.map((u) => (
                    <tr key={u.id} className={`transition-colors group ${!u.is_active ? 'bg-gray-50/50 opacity-75' : 'hover:bg-indigo-50/30'}`}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-4">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold shadow-sm ring-2 ring-white ${
                            u.is_active ? 'bg-gradient-to-br from-indigo-100 to-purple-100 text-indigo-700' : 'bg-gray-200 text-gray-600'
                          }`}>
                            {getInitials(u.full_name, u.email)}
                          </div>
                          <div>
                            <div className={`text-sm font-semibold ${u.is_active ? 'text-gray-900' : 'text-gray-600'}`}>
                              {u.full_name || '—'}
                            </div>
                            <div className="text-sm text-gray-500">{u.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {u.role === 'MANAGER' ? (
                          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-700 border border-purple-200">
                            <Shield className="w-3.5 h-3.5" />Manager
                          </span>
                        ) : u.role === 'TEAM_LEADER' ? (
                          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700 border border-blue-200">
                            <Shield className="w-3.5 h-3.5" />Team Leader
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700 border border-gray-200">
                            Employé
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 font-medium">
                        {u.contract_type || '—'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-700">
                          {u.monthly_gross_salary ? `${Number(u.monthly_gross_salary).toFixed(2)} €` : '—'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex flex-col gap-1.5">
                          <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4 text-indigo-500" />
                            <span className="text-sm font-bold text-gray-900">
                              {Math.max(0, parseFloat(balances[u.id]?.available_days || 0)).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} j
                            </span>
                          </div>
                          {parseFloat(balances[u.id]?.anticipated_days ?? 0) > 0 && (
                            <div className="flex items-center gap-1 text-[11px] text-amber-700 font-semibold bg-amber-100 px-2 py-0.5 rounded-md w-fit">
                              <AlertTriangle className="w-3 h-3" />
                              {balances[u.id]?.anticipated_days} anticipés
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {u.is_active ? (
                          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-green-50 text-green-700 border border-green-200">
                            <div className="w-1.5 h-1.5 rounded-full bg-green-500 shadow-sm shadow-green-500/50"></div>Actif
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-red-50 text-red-700 border border-red-200">
                            <div className="w-1.5 h-1.5 rounded-full bg-red-500 shadow-sm shadow-red-500/50"></div>Inactif
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right relative">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setActionMenuOpenId(actionMenuOpenId === u.id ? null : u.id);
                          }}
                          className="p-2 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                        >
                          <MoreVertical className="w-5 h-5" />
                        </button>
                        
                        {actionMenuOpenId === u.id && (
                          <div className="absolute right-12 top-4 w-48 bg-white rounded-xl shadow-xl border border-gray-100 z-50 py-1 overflow-hidden" onClick={e => e.stopPropagation()}>
                            <button
                              onClick={() => { setActionMenuOpenId(null); handleEdit(u); }}
                              className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-indigo-50 hover:text-indigo-600 flex items-center gap-2.5 transition-colors"
                            >
                              <Edit className="w-4 h-4" /> Éditer le profil
                            </button>
                            <button
                              onClick={() => { setActionMenuOpenId(null); handleOpenAvenant(u); }}
                              className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-indigo-50 hover:text-indigo-600 flex items-center gap-2.5 transition-colors"
                            >
                              <FileText className="w-4 h-4" /> Créer un avenant
                            </button>
                            <button
                              onClick={() => { setActionMenuOpenId(null); navigate(`/utilisateurs/${u.id}/contrat`); }}
                              className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-indigo-50 hover:text-indigo-600 flex items-center gap-2.5 transition-colors"
                            >
                              <History className="w-4 h-4" /> Voir l'historique
                            </button>
                            <div className="h-px bg-gray-100 my-1"></div>
                            <button
                              onClick={() => { setActionMenuOpenId(null); handleDeactivate(u); }}
                              className={`w-full text-left px-4 py-2.5 text-sm flex items-center gap-2.5 transition-colors ${u.is_active ? 'text-red-600 hover:bg-red-50' : 'text-green-600 hover:bg-green-50'}`}
                            >
                              {u.is_active ? <UserX className="w-4 h-4" /> : <UserCheck className="w-4 h-4" />}
                              {u.is_active ? 'Désactiver le compte' : 'Réactiver le compte'}
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Mobile Card View */}
          <div className="md:hidden divide-y divide-gray-100 min-h-[400px]">
            {loading ? (
              Array(5).fill(0).map((_, i) => (
                <div key={i} className="p-5 animate-pulse">
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex gap-4">
                      <div className="w-12 h-12 bg-gray-200 rounded-full"></div>
                      <div className="mt-1">
                        <div className="w-32 h-4 bg-gray-200 rounded mb-2.5"></div>
                        <div className="w-40 h-3 bg-gray-200 rounded"></div>
                      </div>
                    </div>
                    <div className="w-8 h-8 bg-gray-200 rounded-lg"></div>
                  </div>
                  <div className="grid grid-cols-2 gap-3 mb-3">
                    <div className="h-6 bg-gray-200 rounded-full w-24"></div>
                    <div className="h-6 bg-gray-200 rounded-full w-20"></div>
                  </div>
                  <div className="flex justify-between mt-4">
                    <div className="w-20 h-4 bg-gray-200 rounded"></div>
                    <div className="w-16 h-4 bg-gray-200 rounded"></div>
                  </div>
                </div>
              ))
            ) : paginatedUsers.length === 0 ? (
              <div className="px-6 py-12 text-center text-gray-500">
                <UsersIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-lg font-medium text-gray-900">Aucun utilisateur trouvé</p>
              </div>
            ) : (
              paginatedUsers.map((u) => (
                <div key={u.id} className={`p-5 transition-colors relative ${!u.is_active ? 'bg-gray-50/50 opacity-80' : 'hover:bg-indigo-50/30'}`}>
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex items-center gap-3">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center text-base font-bold shadow-sm ring-2 ring-white ${u.is_active ? 'bg-gradient-to-br from-indigo-100 to-purple-100 text-indigo-700' : 'bg-gray-200 text-gray-600'}`}>
                        {getInitials(u.full_name, u.email)}
                      </div>
                      <div>
                        <div className={`text-base font-semibold ${u.is_active ? 'text-gray-900' : 'text-gray-600'}`}>
                          {u.full_name || '—'}
                        </div>
                        <div className="text-sm text-gray-500 mt-0.5">{u.email}</div>
                      </div>
                    </div>
                    <div className="relative">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setActionMenuOpenId(actionMenuOpenId === u.id ? null : u.id);
                        }}
                        className="p-2 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors focus:outline-none"
                      >
                        <MoreVertical className="w-6 h-6" />
                      </button>
                      {actionMenuOpenId === u.id && (
                        <div className="absolute right-0 top-12 w-48 bg-white rounded-xl shadow-xl border border-gray-100 z-50 py-1 overflow-hidden" onClick={e => e.stopPropagation()}>
                          <button
                            onClick={() => { setActionMenuOpenId(null); handleEdit(u); }}
                            className="w-full text-left px-4 py-3 text-sm text-gray-700 hover:bg-indigo-50 hover:text-indigo-600 flex items-center gap-3"
                          >
                            <Edit className="w-4 h-4" /> Éditer le profil
                          </button>
                          <div className="h-px bg-gray-100 my-1"></div>
                          <button
                            onClick={() => { setActionMenuOpenId(null); handleDeactivate(u); }}
                            className={`w-full text-left px-4 py-3 text-sm flex items-center gap-3 ${u.is_active ? 'text-red-600 hover:bg-red-50' : 'text-green-600 hover:bg-green-50'}`}
                          >
                            {u.is_active ? <UserX className="w-4 h-4" /> : <UserCheck className="w-4 h-4" />}
                            {u.is_active ? 'Désactiver' : 'Réactiver'}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex flex-wrap gap-2 mb-4">
                    {u.role === 'MANAGER' && <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-700 border border-purple-200"><Shield className="w-3.5 h-3.5"/> Manager</span>}
                    {u.role === 'TEAM_LEADER' && <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700 border border-blue-200"><Shield className="w-3.5 h-3.5"/> Team Leader</span>}
                    {u.role === 'EMPLOYEE' && <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700 border border-gray-200">Employé</span>}
                    
                    {u.is_active ? (
                      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-green-50 text-green-700 border border-green-200"><div className="w-1.5 h-1.5 rounded-full bg-green-500"></div> Actif</span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-red-50 text-red-700 border border-red-200"><div className="w-1.5 h-1.5 rounded-full bg-red-500"></div> Inactif</span>
                    )}

                    {u.contract_type && <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-gray-50 text-gray-600 border border-gray-200">{u.contract_type}</span>}
                  </div>

                  <div className="grid grid-cols-2 gap-3 mt-4 bg-gray-50 p-3 rounded-xl border border-gray-100">
                    <div className="flex flex-col gap-1">
                      <span className="text-[11px] text-gray-500 uppercase font-bold tracking-wider">Congés</span>
                      <div className="flex items-center gap-1.5">
                        <Calendar className="w-4 h-4 text-indigo-500" />
                        <span className="text-sm font-bold text-gray-900">
                          {Math.max(0, parseFloat(balances[u.id]?.available_days || 0)).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} j
                        </span>
                      </div>
                      {parseFloat(balances[u.id]?.anticipated_days ?? 0) > 0 && (
                         <div className="text-[10px] text-amber-700 font-semibold flex items-center gap-1 mt-1 bg-amber-100/50 w-fit px-1.5 py-0.5 rounded">
                           <AlertTriangle className="w-3 h-3" /> {balances[u.id]?.anticipated_days} ant.
                         </div>
                      )}
                    </div>
                    <div className="flex flex-col gap-1 border-l border-gray-200 pl-3">
                      <span className="text-[11px] text-gray-500 uppercase font-bold tracking-wider">Salaire Mensuel</span>
                      <span className="text-sm font-semibold text-gray-900 mt-0.5">{u.monthly_gross_salary ? `${Number(u.monthly_gross_salary).toFixed(2)} €/mois` : '—'}</span>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
          
          {/* Pagination Controls */}
          {!loading && filteredUsers.length > 0 && (
            <div className="px-4 sm:px-6 py-4 border-t border-gray-100 flex flex-col sm:flex-row items-center justify-between gap-4 bg-gray-50/50 rounded-b-xl">
              <div className="text-sm text-gray-500 text-center sm:text-left">
                Affichage de <span className="font-medium text-gray-900">{(currentPage - 1) * itemsPerPage + 1}</span> à <span className="font-medium text-gray-900">{Math.min(currentPage * itemsPerPage, filteredUsers.length)}</span> sur <span className="font-medium text-gray-900">{filteredUsers.length}</span> résultats
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="p-2 rounded-lg border border-gray-200 bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <div className="flex items-center gap-1 px-1">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) pageNum = i + 1;
                    else if (currentPage <= 3) pageNum = i + 1;
                    else if (currentPage >= totalPages - 2) pageNum = totalPages - 4 + i;
                    else pageNum = currentPage - 2 + i;
                    
                    return (
                      <button
                        key={pageNum}
                        onClick={() => setCurrentPage(pageNum)}
                        className={`w-9 h-9 rounded-lg flex items-center justify-center text-sm font-semibold transition-all ${
                          currentPage === pageNum ? 'bg-indigo-600 text-white shadow-md shadow-indigo-200' : 'bg-transparent text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>
                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="p-2 rounded-lg border border-gray-200 bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4 sm:p-6 opacity-100 transition-opacity">
            <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col transform transition-transform">
              {/* Modal Header */}
              <div className="flex justify-between items-center px-6 py-5 border-b border-gray-100 bg-white z-10 flex-shrink-0">
                <h2 className="text-xl font-bold text-gray-900 flex items-center gap-3">
                  <div className="p-2 bg-indigo-50 rounded-lg text-indigo-600">
                    {isCreating ? <Plus className="w-5 h-5" /> : <Edit className="w-5 h-5" />}
                  </div>
                  {isCreating ? 'Nouvel Utilisateur' : 'Modifier l\'Utilisateur'}
                </h2>
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-gray-200"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Tab bar — only in edit mode */}
              {!isCreating && (
                <div className="flex border-b border-gray-200 bg-white px-6 pt-4">
                  <button
                    type="button"
                    onClick={() => setModalTab('form')}
                    className={`pb-3 px-4 text-sm font-semibold border-b-2 transition-colors ${
                      modalTab === 'form'
                        ? 'border-indigo-500 text-indigo-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    Informations
                  </button>
                  <button
                    type="button"
                    onClick={() => { setModalTab('history'); fetchContractVersions(editingUser?.id); }}
                    className={`pb-3 px-4 text-sm font-semibold border-b-2 transition-colors ${
                      modalTab === 'history'
                        ? 'border-indigo-500 text-indigo-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    Historique contractuel
                  </button>
                </div>
              )}

              {/* Modal Body */}
              <div className="p-6 overflow-y-auto flex-1 bg-gray-50/30">
                {modalTab === 'history' && !isCreating && (
                  <ContractHistoryTab versions={contractVersions} />
                )}
                {modalTab === 'form' && (
                <form id="user-form" onSubmit={handleSubmit} className="space-y-8">
                  {/* Personal Information */}
                  <section className="bg-white p-5 rounded-xl border border-gray-100 shadow-sm">
                    <h3 className="text-xs font-bold text-indigo-600 uppercase tracking-wider mb-4 flex items-center gap-2">
                      <UsersIcon className="w-4 h-4" /> Informations Personnelles
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1.5">Nom complet</label>
                        <input
                          type="text"
                          value={formData.full_name}
                          onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                          className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg focus:bg-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none transition-all"
                          placeholder="Jean Dupont"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1.5">Email <span className="text-red-500">*</span></label>
                        <input
                          type="email"
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg focus:bg-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none transition-all"
                          placeholder="jean@example.com"
                          required
                        />
                      </div>
                    </div>
                  </section>

                  {/* Permissions */}
                  <section className="bg-white p-5 rounded-xl border border-gray-100 shadow-sm">
                    <h3 className="text-xs font-bold text-indigo-600 uppercase tracking-wider mb-4 flex items-center gap-2">
                      <Shield className="w-4 h-4" /> Droits et Permissions
                    </h3>
                    <div className="space-y-5">
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        {[
                          { value: 'EMPLOYEE', label: 'Employé', icon: UsersIcon, color: 'text-gray-600', bg: 'bg-gray-100', border: 'border-gray-200' },
                          { value: 'TEAM_LEADER', label: 'Team Leader', icon: Shield, color: 'text-blue-600', bg: 'bg-blue-100', border: 'border-blue-200' },
                          { value: 'MANAGER', label: 'Manager', icon: Shield, color: 'text-purple-600', bg: 'bg-purple-100', border: 'border-purple-200' },
                        ].map(({ value, label, icon: Icon, color, bg, border }) => (
                          <label
                            key={value}
                            className={`flex flex-col items-center gap-2 p-4 border-2 rounded-xl cursor-pointer transition-all ${
                              formData.role === value
                                ? `border-indigo-500 bg-indigo-50/50 shadow-sm ring-1 ring-indigo-500/20`
                                : `border-gray-100 hover:${border} hover:bg-gray-50`
                            }`}
                          >
                            <input
                              type="radio"
                              name="role"
                              value={value}
                              checked={formData.role === value}
                              onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                              className="sr-only"
                            />
                            <div className={`p-2 rounded-full ${bg} ${color}`}>
                              <Icon className="w-5 h-5" />
                            </div>
                            <div className="text-sm font-semibold text-gray-900">{label}</div>
                          </label>
                        ))}
                      </div>

                      <label className="flex items-center gap-4 p-4 bg-gray-50 border border-gray-200 rounded-xl cursor-pointer hover:bg-gray-100 transition-colors">
                        <div className="relative flex items-center">
                          <input
                            type="checkbox"
                            checked={formData.is_active}
                            onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                            className="peer sr-only"
                          />
                          <div className="w-11 h-6 bg-gray-300 rounded-full peer peer-checked:bg-green-500 transition-colors"></div>
                          <div className="absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform peer-checked:translate-x-5"></div>
                        </div>
                        <div>
                          <div className="font-semibold text-gray-900">Compte actif</div>
                          <div className="text-sm text-gray-500">L'utilisateur peut se connecter et utiliser l'application</div>
                        </div>
                      </label>
                    </div>
                  </section>

                  {/* Contract Information */}
                  <section className="bg-white p-5 rounded-xl border border-gray-100 shadow-sm">
                    <h3 className="text-xs font-bold text-indigo-600 uppercase tracking-wider mb-4 flex items-center gap-2">
                      <Calendar className="w-4 h-4" /> Informations Contractuelles
                    </h3>
                    {!isCreating && (
                      <div className="mb-4 flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-800">
                        <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0 text-amber-600" />
                        <span>
                          Modifier ces champs corrigera le contrat en cours et impactera les calculs financiers de toutes les périodes couvertes.
                          Pour un changement formel avec date d'effet, utilisez <strong>"Créer un avenant"</strong>.
                        </span>
                      </div>
                    )}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div className="sm:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-5">
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-1.5">Type de contrat</label>
                          <div className="relative">
                            <select
                              value={formData.contract_type}
                              onChange={(e) => setFormData({
                                ...formData,
                                contract_type: e.target.value,
                                contract_end_date: e.target.value === 'CDD' ? formData.contract_end_date : '',
                              })}
                              className="w-full pl-4 pr-10 py-2.5 bg-gray-50 border border-gray-200 rounded-lg focus:bg-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none transition-all appearance-none cursor-pointer"
                            >
                              <option value="">-- Sélectionner --</option>
                              <option value="CDI">CDI</option>
                              <option value="CDD">CDD</option>
                              <option value="Freelance">Freelance</option>
                              <option value="CDI_CADRE_HS">CDI CADRE HS</option>
                              <option value="CDI_CADRE_NO_HS">CDI CADRE NO HS</option>
                            </select>
                            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
                          </div>
                        </div>
                        <div>
                          <div className="flex justify-between items-center mb-1.5">
                            <label className="block text-sm font-semibold text-gray-700">Salaire mensuel brut (€)</label>
                            {(() => {
                              const hours = parseFloat(formData.weekly_hours) || 35;
                              const smicVal = (hours * (smicBase / 35)).toFixed(2);
                              return (
                                <button
                                  type="button"
                                  onClick={() => setFormData({ ...formData, monthly_gross_salary: smicVal })}
                                  className="group inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-indigo-50/70 border border-indigo-100 hover:border-indigo-600 text-xs font-semibold text-indigo-700 hover:bg-indigo-600 hover:text-white shadow-sm hover:shadow active:scale-95 transition-all duration-200 cursor-pointer"
                                  title={`Appliquer le SMIC brut pour ${hours}h : ${smicVal} €`}
                                >
                                  <span className="text-xs">🇫🇷</span>
                                  <span>SMIC {hours}h : {Number(smicVal).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €</span>
                                  <Sparkles className="w-3.5 h-3.5 text-indigo-500 group-hover:text-indigo-200 transition-colors" />
                                </button>
                              );
                            })()}
                          </div>
                          <input
                            type="number"
                            value={formData.monthly_gross_salary}
                            onChange={(e) => setFormData({ ...formData, monthly_gross_salary: e.target.value })}
                            min="0" step="0.01"
                            className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg focus:bg-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none transition-all"
                            placeholder="Ex: 2275.00"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-1.5">Charges patronales (€)</label>
                          <input
                            type="number"
                            value={formData.employer_charges}
                            onChange={(e) => setFormData({ ...formData, employer_charges: e.target.value })}
                            min="0" step="0.01"
                            className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg focus:bg-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none transition-all"
                            placeholder="Ex: 900.00"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1.5">Début de contrat</label>
                        <input
                          type="date"
                          value={formData.contract_start_date}
                          onChange={(e) => setFormData({ ...formData, contract_start_date: e.target.value })}
                          className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg focus:bg-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none transition-all cursor-pointer"
                        />
                      </div>
                      
                      {formData.contract_type === 'CDD' && (
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-1.5">Fin de contrat (CDD)</label>
                          <input
                            type="date"
                            value={formData.contract_end_date}
                            onChange={(e) => setFormData({ ...formData, contract_end_date: e.target.value })}
                            className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg focus:bg-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none transition-all cursor-pointer"
                          />
                        </div>
                      )}

                      <div className="sm:col-span-2 bg-amber-50/50 p-4 rounded-xl border border-amber-100">
                        <label className="block text-sm font-semibold text-amber-800 mb-1.5 flex items-center gap-2">
                          <AlertTriangle className="w-4 h-4" /> Fin de période d'essai
                        </label>
                        <input
                          type="date"
                          value={formData.trial_period_end_date}
                          onChange={(e) => setFormData({ ...formData, trial_period_end_date: e.target.value })}
                          className="w-full px-4 py-2.5 bg-white border border-amber-200 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent outline-none transition-all cursor-pointer"
                        />
                      </div>
                    </div>
                  </section>

                  {/* Settings */}
                  <section className="bg-white p-5 rounded-xl border border-gray-100 shadow-sm">
                    <h3 className="text-xs font-bold text-indigo-600 uppercase tracking-wider mb-4 flex items-center gap-2">
                      <RefreshCw className="w-4 h-4" /> Paramètres Avancés
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1.5">Heures hebdomadaires</label>
                        <input
                          type="number"
                          value={formData.weekly_hours}
                          onChange={(e) => setFormData({ ...formData, weekly_hours: e.target.value })}
                          min="0" max="168" step="0.1"
                          className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg focus:bg-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none transition-all"
                          placeholder="Ex: 35"
                        />
                      </div>
                      <div className="sm:col-span-2">
                        <label className="block text-sm font-semibold text-gray-700 mb-1.5">Jours travaillés / semaine</label>
                        <WorkDaysCheckboxes
                          value={formData.work_days}
                          onChange={(days) => setFormData({ ...formData, work_days: days })}
                        />
                      </div>
                      <div className="sm:col-span-2">
                        <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                          Jours de congés disponibles
                        </label>
                        <input
                          type="number"
                          value={formData.leave_balance}
                          onChange={(e) => setFormData({ ...formData, leave_balance: parseFloat(e.target.value) || 0 })}
                          step="0.5"
                          className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none transition-all ${
                            parseFloat(formData.leave_balance) < 0 ? 'border-red-300 bg-red-50 text-red-900' : 'bg-gray-50 border-gray-200 focus:bg-white'
                          }`}
                        />
                        <p className="text-xs text-gray-500 mt-2">Peut être ajusté manuellement, même en négatif si l'employé a pris plus de congés qu'acquis.</p>
                      </div>
                    </div>
                  </section>

                  {/* Password */}
                  <section className="bg-white p-5 rounded-xl border border-gray-100 shadow-sm">
                    <h3 className="text-xs font-bold text-indigo-600 uppercase tracking-wider mb-4 flex items-center gap-2">
                      <Key className="w-4 h-4" /> Sécurité
                    </h3>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                        {isCreating ? 'Mot de passe ' : 'Nouveau mot de passe '}
                        {isCreating && <span className="text-red-500">*</span>}
                      </label>
                      <div className="relative">
                        <input
                          type={showPassword ? "text" : "password"}
                          value={formData.password}
                          onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                          className="w-full pl-4 pr-24 py-2.5 bg-gray-50 border border-gray-200 rounded-lg focus:bg-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none transition-all font-mono"
                          placeholder={isCreating ? 'Minimum 6 caractères' : 'Laisser vide pour ne pas changer'}
                          required={isCreating}
                          minLength={isCreating ? 6 : undefined}
                        />
                        <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="p-1.5 text-gray-400 hover:text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
                            title={showPassword ? "Masquer" : "Afficher"}
                          >
                            {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                          <button
                            type="button"
                            onClick={generatePassword}
                            className="p-1.5 text-indigo-500 hover:text-indigo-700 hover:bg-indigo-100 rounded-md transition-colors"
                            title="Générer un mot de passe aléatoire"
                          >
                            <Key className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                      {!isCreating && (
                        <p className="text-xs text-gray-500 mt-2">Laisser vide pour conserver le mot de passe actuel</p>
                      )}
                    </div>
                  </section>
                </form>
                )}
              </div>

              {/* Modal Footer */}
              <div className="px-6 py-4 border-t border-gray-100 bg-white flex justify-end gap-3 flex-shrink-0">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-5 py-2.5 border border-gray-200 bg-white text-gray-700 font-semibold rounded-xl hover:bg-gray-50 hover:border-gray-300 transition-all shadow-sm"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  form="user-form"
                  disabled={submitting}
                  className="flex items-center gap-2 px-6 py-2.5 bg-indigo-600 text-white font-semibold rounded-xl hover:bg-indigo-700 transition-all disabled:opacity-70 disabled:cursor-not-allowed shadow-sm shadow-indigo-200"
                >
                  {submitting ? (
                    <RefreshCw className="w-5 h-5 animate-spin" />
                  ) : (
                    <Check className="w-5 h-5" />
                  )}
                  {submitting ? 'Enregistrement...' : 'Enregistrer'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Avenant Modal */}
        {showAvenantModal && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
              <div className="flex items-center justify-between p-6 border-b border-gray-100">
                <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-indigo-600" />
                  Créer un avenant — {avenantUser?.full_name || avenantUser?.email}
                </h2>
                <button
                  type="button"
                  onClick={() => setShowAvenantModal(false)}
                  className="p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600 rounded-full transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="p-6 overflow-y-auto flex-1">
                <form id="avenant-form" onSubmit={handleSubmitAvenant} className="space-y-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                      Date d'effet <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      required
                      value={avenantForm.effective_from}
                      onChange={(e) => setAvenantForm({ ...avenantForm, effective_from: e.target.value })}
                      className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg focus:bg-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none transition-all cursor-pointer"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">Motif / Note</label>
                    <input
                      type="text"
                      value={avenantForm.note}
                      onChange={(e) => setAvenantForm({ ...avenantForm, note: e.target.value })}
                      placeholder="Ex: Augmentation annuelle"
                      className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg focus:bg-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none transition-all"
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1.5">Type de contrat</label>
                      <select
                        value={avenantForm.contract_type}
                        onChange={(e) => setAvenantForm({ ...avenantForm, contract_type: e.target.value })}
                        className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg focus:bg-white focus:ring-2 focus:ring-indigo-500 outline-none transition-all"
                      >
                        <option value="">-- Sélectionner --</option>
                        <option value="CDI">CDI</option>
                        <option value="CDD">CDD</option>
                        <option value="Freelance">Freelance</option>
                        <option value="CDI_CADRE_HS">CDI CADRE HS</option>
                        <option value="CDI_CADRE_NO_HS">CDI CADRE NO HS</option>
                      </select>
                    </div>
                    <div>
                      <div className="flex justify-between items-center mb-1.5">
                        <label className="block text-sm font-semibold text-gray-700">Salaire mensuel brut (€)</label>
                        {(() => {
                          const hours = parseFloat(avenantForm.weekly_hours) || 35;
                          const smicVal = (hours * (smicBase / 35)).toFixed(2);
                          return (
                            <button
                              type="button"
                              onClick={() => setAvenantForm({ ...avenantForm, monthly_gross_salary: smicVal })}
                              className="group inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-indigo-50/70 border border-indigo-100 hover:border-indigo-600 text-xs font-semibold text-indigo-700 hover:bg-indigo-600 hover:text-white shadow-sm hover:shadow active:scale-95 transition-all duration-200 cursor-pointer"
                              title={`Appliquer le SMIC brut pour ${hours}h : ${smicVal} €`}
                            >
                              <span className="text-xs">🇫🇷</span>
                              <span>SMIC {hours}h : {Number(smicVal).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €</span>
                              <Sparkles className="w-3.5 h-3.5 text-indigo-500 group-hover:text-indigo-200 transition-colors" />
                            </button>
                          );
                        })()}
                      </div>
                      <input type="number" min="0" step="0.01"
                        value={avenantForm.monthly_gross_salary}
                        onChange={(e) => setAvenantForm({ ...avenantForm, monthly_gross_salary: e.target.value })}
                        placeholder="Ex: 2500.00"
                        className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg focus:bg-white focus:ring-2 focus:ring-indigo-500 outline-none transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1.5">Charges patronales (€)</label>
                      <input type="number" min="0" step="0.01"
                        value={avenantForm.employer_charges}
                        onChange={(e) => setAvenantForm({ ...avenantForm, employer_charges: e.target.value })}
                        placeholder="Ex: 1000.00"
                        className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg focus:bg-white focus:ring-2 focus:ring-indigo-500 outline-none transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1.5">Heures / semaine</label>
                      <input type="number" min="0" max="168" step="0.1"
                        value={avenantForm.weekly_hours}
                        onChange={(e) => setAvenantForm({ ...avenantForm, weekly_hours: e.target.value })}
                        placeholder="Ex: 35"
                        className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg focus:bg-white focus:ring-2 focus:ring-indigo-500 outline-none transition-all"
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <label className="block text-sm font-semibold text-gray-700 mb-1.5">Jours travaillés / semaine</label>
                      <WorkDaysCheckboxes
                        value={avenantForm.work_days}
                        onChange={(days) => setAvenantForm({ ...avenantForm, work_days: days })}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1.5">Début de contrat</label>
                      <input type="date"
                        value={avenantForm.contract_start_date}
                        onChange={(e) => setAvenantForm({ ...avenantForm, contract_start_date: e.target.value })}
                        className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg focus:bg-white focus:ring-2 focus:ring-indigo-500 outline-none transition-all cursor-pointer"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1.5">Fin de période d'essai</label>
                      <input type="date"
                        value={avenantForm.trial_period_end_date}
                        onChange={(e) => setAvenantForm({ ...avenantForm, trial_period_end_date: e.target.value })}
                        className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg focus:bg-white focus:ring-2 focus:ring-indigo-500 outline-none transition-all cursor-pointer"
                      />
                    </div>
                  </div>
                </form>
              </div>

              <div className="p-6 border-t border-gray-100 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowAvenantModal(false)}
                  className="px-5 py-2.5 text-sm font-semibold text-gray-700 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  form="avenant-form"
                  disabled={submittingAvenant}
                  className="px-5 py-2.5 text-sm font-semibold text-white bg-indigo-600 rounded-xl hover:bg-indigo-700 disabled:opacity-50 transition-colors flex items-center gap-2"
                >
                  <Check className="w-4 h-4" />
                  {submittingAvenant ? 'Enregistrement...' : "Créer l'avenant"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ContractHistoryTab({ versions }) {
  if (versions.length === 0) {
    return (
      <div className="p-8 text-center text-gray-500">
        <p className="text-sm">Aucun historique contractuel disponible.</p>
      </div>
    );
  }
  return (
    <div className="overflow-y-auto">
      <div className="overflow-x-auto rounded-xl border border-gray-100">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-xs font-semibold text-gray-500 uppercase tracking-wider">
            <tr>
              <th className="px-4 py-3 text-left">Date d'effet</th>
              <th className="px-4 py-3 text-left">Salaire brut</th>
              <th className="px-4 py-3 text-left">Charges pat.</th>
              <th className="px-4 py-3 text-left">Heures / sem.</th>
              <th className="px-4 py-3 text-left">Type</th>
              <th className="px-4 py-3 text-left">Note</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {versions.map((v) => (
              <tr key={v.id} className="hover:bg-indigo-50/30 transition-colors">
                <td className="px-4 py-3 font-medium text-gray-800">
                  {v.effective_from
                    ? new Date(v.effective_from).toLocaleDateString('fr-FR')
                    : <span className="text-gray-400 italic">Initial</span>}
                </td>
                <td className="px-4 py-3">
                  {v.monthly_gross_salary ? `${Number(v.monthly_gross_salary).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €` : '—'}
                </td>
                <td className="px-4 py-3">
                  {v.employer_charges ? `${Number(v.employer_charges).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €` : '—'}
                </td>
                <td className="px-4 py-3">{v.weekly_hours ? `${v.weekly_hours}h` : '—'}</td>
                <td className="px-4 py-3">{v.contract_type || '—'}</td>
                <td className="px-4 py-3 text-gray-500 italic">{v.note || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
